/**
 * Cooling stances — the heat-rejection sink (`bopHeatRejection`) and the
 * rack-side loop (`coolingStrategy`), from basin water stress, declared water
 * facts, the Stage 1 water findings and the workload density anchors.
 *
 * Water stress is the WRI Aqueduct 4.0 category already frozen in the SED
 * (−1 arid & low use, 0 low <10%, 1 low-medium, 2 medium-high 20–40%,
 * 3 high 40–80%, 4 extremely high >80%). WRI leaves decision thresholds to
 * the user; treating "high" and above as a constraint on evaporative cooling
 * is platform screening policy, stated as such, and still needs site-specific
 * confirmation.
 */
import type { ProjectIntent, SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { formatDroughtExposedSources, hasDroughtExposedSource } from '../intent/waterSource';
import type { FindingClaim } from '../findingClaim';
import { resolveWaterStressBand } from './waterStressBand';
import { combineStances } from './powerStances';
import type { StanceReason, Stage2OptionAssessment } from './stage2Types';
import { stripNarrativeArtifacts } from '../reportRendering/narrativeArtifacts';

export type CoolingSinkOption = 'dry-cooler' | 'wet-cooling-tower' | 'hybrid' | 'once-through';
export type CoolingLoopOption = 'liquid' | 'hybrid' | 'air';

const SINK_LABEL: Readonly<Record<CoolingSinkOption, string>> = {
  'dry-cooler': 'Heat rejection — dry coolers',
  'wet-cooling-tower': 'Heat rejection — evaporative cooling towers',
  hybrid: 'Heat rejection — hybrid wet/dry',
  'once-through': 'Heat rejection — once-through water',
};

const LOOP_LABEL: Readonly<Record<CoolingLoopOption, string>> = {
  liquid: 'Rack cooling — direct liquid',
  hybrid: 'Rack cooling — hybrid air + liquid',
  air: 'Rack cooling — air',
};

export interface CoolingStanceInputs {
  intent: ProjectIntent | null | undefined;
  sed: SiteEvidenceDocument | null | undefined;
  feasibility: SiteRagReport | null;
}

type StressBand = 'arid' | 'low' | 'medium-high' | 'high' | null;

function stressBand(sed: SiteEvidenceDocument | null | undefined): { band: StressBand; label: string } {
  const water = sed?.waterStress?.data;
  if (!water) return { band: null, label: '' };
  const { category, label } = resolveWaterStressBand(water);
  if (category === -1) return { band: 'arid', label };
  if (typeof category === 'number' && category >= 3) return { band: 'high', label };
  if (category === 2) return { band: 'medium-high', label };
  if (category === 0 || category === 1) return { band: 'low', label };
  if (water.stressLevel === 'High') return { band: 'high', label };
  return { band: null, label: '' };
}

/** Confirmed or likely Stage 1 water risks (legacy string entries are skipped). */
export function screeningWaterRisks(report: SiteRagReport | null): FindingClaim[] {
  const risks = (report?.waterAnalysis?.risks ?? []) as ReadonlyArray<FindingClaim | string>;
  return risks.filter((risk): risk is FindingClaim =>
    typeof risk === 'object' && risk !== null && typeof risk.claim === 'string'
      && (risk.evidenceState === 'confirmed' || risk.evidenceState === 'likely_present_unverified'));
}

function sinkReasons(option: CoolingSinkOption, inputs: CoolingStanceInputs): StanceReason[] {
  const reasons: StanceReason[] = [];
  const { band, label } = stressBand(inputs.sed);
  const water = inputs.intent?.waterSource;
  const evaporative = option === 'wet-cooling-tower';

  if (option === 'once-through') {
    return [{
      text: 'Once-through cooling withdraws about 45 times more water than towers (NREL medians for gas combined cycle: 11,380 vs 253 gal/MWh) and depends on a large surface-water intake.',
      basis: 'industry-evidence',
      effect: 'constrained',
      sourceIds: ['nrel-water-factors-2011'],
    }];
  }

  if (band === 'high' || band === 'arid') {
    if (evaporative) {
      reasons.push({
        text: band === 'arid'
          ? 'The basin is arid with low water use (WRI Aqueduct): physical supply is scarce, so evaporative cooling would add a consumptive demand.'
          : `Baseline water stress is ${label} (WRI Aqueduct): withdrawals already use 40% or more of available supply, so evaporative cooling would add a consumptive demand in a stressed basin.`,
        basis: 'screening-policy',
        effect: 'constrained',
        sourceIds: ['wri-aqueduct-4'],
        evidencePath: 'sed.waterStress',
      });
    } else if (option === 'dry-cooler') {
      reasons.push({
        text: 'Dry heat rejection consumes almost no water (NREL: about 2 gal/MWh dry-cooled vs 198 with towers for gas combined cycle); closed-loop chip-level designs evaporate none (Microsoft, 2024).',
        basis: 'industry-evidence',
        effect: 'favoured',
        sourceIds: ['nrel-water-factors-2011', 'microsoft-zero-water-cooling'],
      });
    } else {
      reasons.push({
        text: 'Hybrid wet/dry rejection limits evaporation to the hottest hours.',
        basis: 'screening-policy',
        effect: 'viable',
      });
    }
  } else if (band === 'medium-high') {
    reasons.push({
      text: `Baseline water stress is ${label} (WRI Aqueduct, 20–40%)${evaporative ? ': confirm supply and rights before relying on evaporative cooling' : ''}.`,
      basis: 'site-evidence',
      effect: 'viable',
      sourceIds: ['wri-aqueduct-4'],
      evidencePath: 'sed.waterStress',
    });
  } else if (band === 'low') {
    reasons.push({
      text: `Baseline water stress is ${label} (WRI Aqueduct).`,
      basis: 'site-evidence',
      effect: 'viable',
      sourceIds: ['wri-aqueduct-4'],
      evidencePath: 'sed.waterStress',
    });
    if (evaporative && water?.rightsStatus === 'permitted') {
      reasons.push({
        text: 'Low basin stress and permitted water rights are recorded.',
        basis: 'owner-input',
        effect: 'favoured',
      });
    }
  }

  if (evaporative) {
    if (water?.source === 'none') {
      reasons.push({ text: 'The owner records no water source identified.', basis: 'owner-input', effect: 'constrained' });
    } else if (hasDroughtExposedSource(water)) {
      reasons.push({
        text: `The declared ${formatDroughtExposedSources(water)} supply is drought-exposed.`,
        basis: 'owner-input',
        effect: 'viable',
      });
    }
    for (const risk of screeningWaterRisks(inputs.feasibility).slice(0, 2)) {
      reasons.push({
        text: `Screening water finding: ${stripNarrativeArtifacts(risk.claim).trim()}`,
        basis: 'screening-report',
        effect: 'viable',
        evidencePath: inputs.feasibility ? `report:${inputs.feasibility.id}.waterAnalysis.risks` : undefined,
      });
    }
  }
  return reasons;
}

function loopReasons(option: CoolingLoopOption, intent: ProjectIntent | null | undefined): StanceReason[] {
  const workload = intent?.workloadProfile;
  if (workload === 'ai-training') {
    if (option === 'liquid') {
      return [{ text: 'AI training is planned at 50–100 kW/rack with liquid (CDU) cooling.', basis: 'screening-policy', effect: 'favoured', sourceIds: ['platform-workload-profiles'] }];
    }
    if (option === 'air') {
      return [{ text: 'Air cooling is not the planning basis at AI-training rack densities (50–100 kW/rack).', basis: 'screening-policy', effect: 'constrained', sourceIds: ['platform-workload-profiles'] }];
    }
    return [{ text: 'Hybrid air + liquid can serve mixed halls at AI-training densities.', basis: 'screening-policy', effect: 'viable', sourceIds: ['platform-workload-profiles'] }];
  }
  if (workload === 'hpc') {
    return [{ text: 'HPC is planned at 30–50 kW/rack.', basis: 'screening-policy', effect: 'viable', sourceIds: ['platform-workload-profiles'] }];
  }
  if (workload === 'ai-inference') {
    return [{ text: 'AI inference is planned at 15–25 kW/rack; hybrid cooling is the planning basis.', basis: 'screening-policy', effect: option === 'hybrid' ? 'favoured' : 'viable', sourceIds: ['platform-workload-profiles'] }];
  }
  if (workload === 'enterprise' || workload === 'colocation' || workload === 'edge') {
    return [{ text: 'This workload is planned at conventional rack densities.', basis: 'screening-policy', effect: 'viable', sourceIds: ['platform-workload-profiles'] }];
  }
  return [];
}

const SINK_ORDER: readonly CoolingSinkOption[] = ['dry-cooler', 'hybrid', 'wet-cooling-tower', 'once-through'];
const LOOP_ORDER: readonly CoolingLoopOption[] = ['liquid', 'hybrid', 'air'];

/** Option ids are prefixed `sink:` / `loop:` so the panel can group them. */
export function assessCoolingOptions(inputs: CoolingStanceInputs): Stage2OptionAssessment[] {
  return [
    ...SINK_ORDER.map(option => {
      const reasons = sinkReasons(option, inputs);
      return { optionId: `sink:${option}`, label: SINK_LABEL[option], stance: combineStances(reasons), reasons };
    }),
    ...LOOP_ORDER.map(option => {
      const reasons = loopReasons(option, inputs.intent);
      return { optionId: `loop:${option}`, label: LOOP_LABEL[option], stance: combineStances(reasons), reasons };
    }),
  ];
}
